/**
 * PolicyArea — "What is covered, for how much, and over what period?"
 *
 * THREE FACTS AND ONE DOCUMENT. The insured value is what the vehicle is covered for, the period is
 * the two days every other figure on this dossier is judged against, and the certificate is the
 * evidence of both. They are one block because they are one piece of paper: the period is read off
 * the certificate, a certificate naming another car is refused against this contract, and a Discard
 * that cleared the file while leaving the dates it was read from would leave the two disagreeing
 * with nothing on screen saying why (`AREA_FIELDS.policy`).
 *
 * THE CERTIFICATE ROW IS THE PAGE'S, MOUNTED ONCE, AND THIS CANVAS ONLY PLACES ITS PIECES. The hook
 * is `useCertificateRow` — the same machinery contract setup's Insurance canvas draws — and it is
 * called by the page rather than here for three reasons, each of which is a defect if it is called
 * here instead:
 *
 *  - THE CREATE BAR IS ON ANOTHER CANVAS. It names `rejection` or `standsUnticked` in its hint, and
 *    a hook mounted on this canvas has no values at all while the admin is on the Advance area
 *    pressing Create.
 *  - THE READS MUST SURVIVE AN AREA SWITCH. The row holds a read in flight, the published triple
 *    and the prefill that names it; unmounting on every move between areas would restart the
 *    on-file read and drop a read held through a commit.
 *  - ONE ROW PER DOCUMENT, WHICH IS THE ROW'S OWN INVARIANT 13. `ocrOnNextFiledRead` is a
 *    module-global one-shot, and a single page-level mount makes that true by construction.
 *
 * So the page owns the hook, `reset()` is reachable from its Discard without a ref, and this file
 * places `upload` and `attestBlock` above the two dates the read fills, `periodBanner` under both of
 * them because the sentence is about the SPAN rather than either end, and `footer` outside the block
 * beside the link that opens the document — which is where all four have always been.
 *
 * THE PERIOD FREEZES AND THE OTHER TWO NEVER DO [F7]. Both instruments print the cover period, so a
 * signed one cannot have it moved underneath it; neither prints the insured value or the
 * certificate, so an admin correcting a typo'd insured value on an executed year is fixing a record
 * rather than rewriting a document.
 */

import { useState, type ReactNode } from 'react';
import { format } from 'date-fns';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

import type { InsuranceInstrument } from '@/lib/agreements/instrument';
import { parseBusinessDay } from '@/lib/format';

import { Block, BlockActions, Blocks, Hint, LockLine, Rec, Rows } from '../../areas/blocks';
import type { CertificateRowModel } from '../../inline/CertificateRow';
import QuietField from '../../inline/QuietField';
import type { InsuranceFormValues } from '../insuranceForm';
import { frozenLock } from './lockCopy';

interface Props {
    mode: 'create' | 'edit';
    /** The instrument is signed: the cover period below is a read [F7]. */
    frozen: boolean;
    /** Which instrument this year is on — the frozen line names the document by its own title. */
    instrument: InsuranceInstrument;
    form: InsuranceFormValues;
    onChange: (updates: Partial<InsuranceFormValues>) => void;
    /** The shared certificate row's pieces. Mounted by the page — see the head. */
    certificate: CertificateRowModel;
    /**
     * The write's own refusal for one of this area's fields, by FORM FIELD NAME.
     *
     * `attributeInsuranceRefusal('policy', message)` is what fills it, and the certificate family
     * lands on `certificateFile` — which the page hands to the row as its `serverError` rather than
     * to any control here.
     */
    errors?: Record<string, string>;
    /** How many junctions this contract carries [D-B17]. Data, never a lock. */
    chainLength: number;
    /** `delete_insurance_cascade`, behind the confirm below. Absent on the create flow. */
    onDelete?: () => void;
    deleting?: boolean;
    /** The server's refusal of the last delete, printed verbatim inside the dialog. */
    deleteRefusal?: string | null;
    /**
     * The dialog has been dismissed — retire the refusal above with it.
     *
     * THE SENTENCE BELONGS TO THE SURFACE IT WAS RAISED ON. It is held by the page, because the
     * mutation that raises it is; the dialog is owned here, because the button that opens it is. So
     * the close is what has to travel, and without it a refused delete followed by Cancel left the
     * server's sentence standing for the life of the page — reappearing under the heading the next
     * time the dialog was opened, as though this attempt had just raised it.
     */
    onDeleteDialogClose?: () => void;
    /** The edit commit bar for this area, drawn by the page while the block is dirty. */
    commit?: ReactNode;
}

/**
 * One end of the period as a read.
 *
 * PARSED FIELD BY FIELD, like every other `'yyyy-MM-dd'` on these dossiers: `new Date('2026-04-17')`
 * is UTC midnight read back with LOCAL getters, which walks a cover date back a day west of
 * Greenwich — on the row whose whole job is to say which day cover starts.
 */
function dayText(value: string): string | null {
    const day = parseBusinessDay(value);
    return day ? format(day, 'MMM d, yyyy') : null;
}

export default function PolicyArea({
    mode,
    frozen,
    instrument,
    form,
    onChange,
    certificate,
    errors,
    chainLength,
    onDelete,
    deleting,
    deleteRefusal,
    onDeleteDialogClose,
    commit,
}: Props) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const lock = frozenLock(instrument);

    return (
        <Blocks>
            <Block id="b-policy" title="Cover" aside="required">
                <p className="sd-lede sd-dim">
                    What the insurer covers this vehicle for, and the year it covers. Both
                    instruments print the period; the insured value is a record of what the car is
                    covered for and appears on neither.
                </p>

                <Rows>
                    <QuietField
                        id="f-insValue"
                        label="Insured value"
                        value={form.valuedAt}
                        onValueChange={(value) => onChange({ valuedAt: value })}
                        inputMode="decimal"
                        placeholder="—"
                        format="GHS"
                        error={errors?.valuedAt}
                    />
                    {/* ABOVE THE DATES IT FILLS, and that placement is the mitigation: on the happy
                        path the certificate is chosen before either date is typed, so the overwrite
                        the read is entitled to make is rarely an overwrite at all. */}
                    {certificate.upload}
                    {certificate.attestBlock}
                </Rows>

                {frozen ? (
                    <>
                        <LockLine title={lock.title}>{lock.body}</LockLine>
                        <Rows>
                            <Rec label="Cover starts" value={dayText(form.startsAt)} empty="Not set" />
                            <Rec
                                label="Cover expires"
                                value={dayText(form.expiresAt)}
                                empty="Not set"
                            />
                        </Rows>
                    </>
                ) : (
                    <Rows>
                        {/* BOTH DATES POINT AT THE PERIOD WARNING WHILE IT STANDS. The sentence is
                            about the SPAN, so it belongs to neither field on its own and to both of
                            them together: without this it is a paragraph below two inputs that a
                            screen reader arrives at only after passing the controls it is about.
                            `aria-describedby` and not `aria-errormessage` — the period never refuses
                            a save, and `QuietInput` spreads unknown props onto the `input` BEFORE it
                            sets its own `aria-invalid`, so this cannot displace a server refusal. */}
                        <QuietField
                            id="f-insStart"
                            label="Cover starts"
                            type="date"
                            value={form.startsAt}
                            onValueChange={(value) => onChange({ startsAt: value })}
                            error={errors?.startsAt}
                            aria-describedby={certificate.dateDescribedBy}
                        />
                        <QuietField
                            id="f-insExpiry"
                            label="Cover expires"
                            type="date"
                            value={form.expiresAt}
                            onValueChange={(value) => onChange({ expiresAt: value })}
                            error={errors?.expiresAt}
                            aria-describedby={certificate.dateDescribedBy}
                        />
                    </Rows>
                )}

                {certificate.periodBanner}

                {/* OUTSIDE THE FIELDS AND UNDER THE WHOLE BLOCK, WHICH IS WHERE IT HAS ALWAYS BEEN.
                    `Open the certificate` and `Check again` are about the document ALREADY ON FILE —
                    the rows above are the draft being typed — and the sentences they raise are read
                    beside them. */}
                {certificate.footer}

                {mode === 'create' && (
                    <Hint className="mt-3">
                        Nothing is written until the policy is created, and that happens on the
                        Advance area — where the premium it is written over is stated.
                    </Hint>
                )}
            </Block>

            {/*
             * THE RENEWAL CHAIN IS A LINE AND NEVER A LOCK [D-B17]. The owner ruled on 2026-08-05
             * that a chained policy stays editable; what an admin needs is to know which year they
             * are looking at, because the figures on a renewal year and on the year the contract was
             * activated under are both correct and different.
             */}
            {chainLength > 1 && (
                <Block id="b-chain" title="Renewal chain" aside="never printed">
                    <p className="sd-lede sd-dim">
                        This contract carries {chainLength} years of cover. This page is about one of
                        them, and every one of them stays editable — the others are on the contract’s
                        Insurance tab, each with its own premium, its own advance and its own
                        instrument.
                    </p>
                </Block>
            )}

            {/*
             * DELETE STANDS ON AN EXISTING POLICY AND NOWHERE ELSE. On the create flow there is
             * nothing to delete, and the cascade below is not a tidy-up: it removes real bank
             * transactions.
             */}
            {mode === 'edit' && onDelete && (
                <BlockActions>
                    <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
                        Delete this policy
                    </Button>
                </BlockActions>
            )}

            <AlertDialog
                open={confirmOpen}
                onOpenChange={(open) => {
                    setConfirmOpen(open);
                    // A REFUSAL DOES NOT OUTLIVE THE DIALOG IT WAS RAISED IN. Cancel, Escape and a
                    // click outside all come through here, and any of them ends the attempt the
                    // sentence was about.
                    if (!open) onDeleteDialogClose?.();
                }}
            >
                <AlertDialogContent className="sd-tokens">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this insurance policy?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete the insurance policy and{' '}
                            <strong>all linked records</strong> including premiums, discounts,
                            advances, deposits, repayments, instalments, agreements, and their
                            associated bank transactions. It also removes any agreement signed in
                            the app, together with the signatures on it. This action cannot be
                            undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    {/* VERBATIM, WHERE THE DECISION WAS TAKEN. The server refuses a policy another
                        contract is also on, and that is a fact about the estate no sentence written
                        here could improve. */}
                    {deleteRefusal && <p className="sd-f-err">{deleteRefusal}</p>}
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(event) => {
                                // The dialog would close itself on click; the write decides when it
                                // closes, so a refusal does not leave the admin staring at a
                                // dismissed surface with no explanation.
                                event.preventDefault();
                                onDelete?.();
                            }}
                            disabled={deleting}
                        >
                            {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Delete everything
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* THE BAR IS THE CANVAS'S LAST CHILD AND NOT THE BLOCK'S, as on every other dossier
                area. `.sd-commit` is sticky to the bottom of its containing block, so a bar nested
                inside `b-policy` scrolls away with that block the moment the canvas is longer than
                the viewport — which it is, on a year with a renewal chain and a Delete under it. */}
            {commit}
        </Blocks>
    );
}
